import axios from "axios";
import styles from "../../../styles/InformationsForm.module.scss";
import { useState, useEffect } from "react";
import AddressInput from "./AddressInput";
import { Dialog, DialogContent, DialogTitle, DialogActions } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { MuiTelInput } from 'mui-tel-input'
import { globalRegex } from "../../../utils/utils";

const BFC = ["bourgogne-franche-comté", "bourgogne franche comté", "bourgogne", "franche comté", "franche-comté"];

function InformationsForm({ onSubmit }) {
  const [jobs, setJobs] = useState([]);
  const [specialities, setSpecialities] = useState([]);

  const [formData, setFormData] = useState({
    lastname: "",
    firstname: "",
    email: "",
    phone: "",
    job: "",
    speciality: "",
    structure: "",
    address: "",
    isProfessional: false,
    acceptCGV: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [isCertified, setIsCertified] = useState(false);

  useEffect(() => {
    const url = process.env.REACT_APP_NUVEE_BASE_URL + `/professional-types`;
    let params = "?fields[0]=name";
    params += "&sort=name:asc";
    axios.get(url + params)
    .then(response => {
      setJobs(response.data ?? []);
    })
    .catch(error => {
      console.error(error);
    });
  }, [])

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      speciality: "",
    }));
      
    if (formData.job) {
      const url = process.env.REACT_APP_NUVEE_BASE_URL + `/specialities`;
      let params = "?fields[0]=name";
      params += `&filters[professional_type][id]=${formData.job}`;
      params += "&sort=name:asc";
      axios.get(url + params)
      .then(response => {
        setSpecialities(response.data ?? []);
      })
      .catch(error => {
        console.error(error);
      });
    } else {
      setSpecialities([]);
    }
  }, [formData.job])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleAddressChange = (newAddress) => {
    if (formData.address !== newAddress.fullAddress) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        address: ""
      }));
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      address: newAddress.fullAddress
    }));
    setCurrentRegion(newAddress.region || null);
  };

  const handleCertifiedChange = (event) => {
    setIsCertified(event.target.checked);
  }

  const testEmail = async (email) => {
    const res = await axios.get(process.env.REACT_APP_NUVEE_BASE_URL + `/agreementsdatum/testemail/${email}`);
    return res?.data?.ok;
  }

  const validate = async () => {
    const newErrors = {};

    if (!formData.lastname.trim()) {
      newErrors.lastname = "Le nom est requis.";
    }

    if (!formData.firstname.trim()) {
      newErrors.firstname = "Le prénom est requis.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis.";
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)
    ) {
      newErrors.email = "L'email est invalide.";
    } else if (
      await testEmail(formData.email.trim()) == false
    ) {
      newErrors.email = "L'email est déjà utilisée, veuillez vous connecter directement à Nuvee ou contacter le support.";
    }

    const phoneValue = (formData.phone || "").replace(/\s/g, "").trim(); // Remove spaces
    if (!phoneValue) newErrors.phone = "Le numéro de téléphone est requis.";
    else if (!phoneValue.startsWith("+")) {
      newErrors.phone =  "Veuillez renseigner un numéro de téléphone au format international (ex: +330612345678)";
    } else if (!globalRegex.phoneRegex.test(phoneValue)) {
      newErrors.phone = "Le numéro de téléphone est invalide.";
    }

    if (jobs.length > 0 && !formData.job) {
      newErrors.job = "Veuillez sélectionner une profession.";
    }

    if (specialities.length > 0 && !formData.speciality) {
      newErrors.speciality = "Veuillez sélectionner une spécialité.";
    }

    if (!formData.structure.trim()) {
      newErrors.structure = "La structure est requise.";
    }

    if (!formData.address?.trim()) {
      newErrors.address = "L'adresse est requise.";
    }

    if (!formData.isProfessional) {
      newErrors.isProfessional =
        "Veuillez certifier être un professionnel de santé.";
    }

    if (!formData.acceptCGV) {
      newErrors.acceptCGV =
        "Veuillez accepter les conditions générales de vente.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (await validate()) {
      try {
        if (currentRegion && BFC.includes(currentRegion.trim().toLowerCase())) {
          setIsDialogOpen(true);
        } else {
          await onSubmit(formData);
        }
      } catch(error) {
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          element.focus();
        }
      }

      setIsSubmitting(false);
    }
  };

  return (
    <form className={styles.informationsForm} onSubmit={handleSubmit}>
      <h2 className={styles.title}>
        Merci de renseigner vos informations pour finaliser la création de votre
        compte
      </h2>

      <Dialog
        className={styles.dialog}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <DialogTitle className={styles.dialogHeader}>
          Bonne nouvelle !
          <FontAwesomeIcon
            className={styles.closeButton}
            icon={faXmark}
            onClick={() => setIsDialogOpen(false)}
          />
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <p>
            Vous exercez en Bourgogne-Franche-Comté et, à ce titre, vous
            bénéficiez d'un <span>accès gratuit</span> à Nuvee ! 🎉
          </p>
          <p>
            Grâce à une licence régionale financée par l'ARS BFC, 
            tous les professionnels de santé de la région peuvent bénéficier
            gratuitement de Nuvee :&nbsp;
            <span>
              aucune démarche à effectuer de votre côté !
            </span>
          </p>
          <blockquote className={styles.quote}>
            <p>✔️ Un accès illimité à toutes nos thématiques.</p>
            <p>
              ✔️ <span>Nombre illimité de patients à inscrire sur Nuvee.</span>
            </p>
          </blockquote>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={isCertified}
              onChange={handleCertifiedChange}
              className={styles.checkbox}
            />
            <span className={styles.customCheckbox}></span>
            <span>
              Je certifie que l'adresse renseignée en Bourgogne-Franche-Comté
              est bien mon lieu d'exercice.
            </span>
          </label>
        </DialogContent>
        <DialogActions className={styles.dialogFooter}>
          <button
            className={styles.button}
            disabled={!isCertified || isSubmitting}
            onClick={() => {
              setIsSubmitting(true);
              onSubmit(formData, true)
            }}
          >
            {isSubmitting ? (
              <>
                Validation en cours{" "}
                <div className={styles.buttonLoader}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 50 50"
                    xmlns="http://www.w3.org/2000/svg"
                    className={styles.spinner}
                  >
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      strokeWidth="5"
                      stroke="#fff"
                      strokeLinecap="round"
                      strokeDasharray="90, 150"
                      strokeDashoffset="0"
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        dur="1.5s"
                        values="-35; 175"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                </div>
              </>
            ) : (
              "J'ai compris"
            )}
          </button>
        </DialogActions>
      </Dialog>

      <div className={styles.group}>
        <div className={styles.field}>
          <div className={styles.inputContainer}>
            <input
              type="text"
              id="lastname"
              name="lastname"
              value={formData.lastname}
              onChange={handleChange}
              className={`${styles.input} ${
                formData.lastname ? styles.filled : ""
              } ${errors.lastname ? styles.errorInput : ""}`}
            />
            <label
              htmlFor="lastname"
              className={`${styles.label} ${
                errors.lastname ? styles.errorLabel : ""
              }`}
            >
              Nom d&apos;usage <span className={styles.required}>*</span>
            </label>
          </div>
          {errors.lastname && (
            <span className={styles.errorMessage}>{errors.lastname}</span>
          )}
        </div>
        <div className={styles.field}>
          <div className={styles.inputContainer}>
            <input
              type="text"
              id="firstname"
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              className={`${styles.input} ${
                formData.firstname ? styles.filled : ""
              } ${errors.firstname ? styles.errorInput : ""}`}
            />
            <label
              htmlFor="firstname"
              className={`${styles.label} ${
                errors.firstname ? styles.errorLabel : ""
              }`}
            >
              Prénom <span className={styles.required}>*</span>
            </label>
          </div>
          {errors.firstname && (
            <span className={styles.errorMessage}>{errors.firstname}</span>
          )}
        </div>
      </div>

      <div className={styles.group}>
        <div className={styles.field}>
          <div className={styles.inputContainer}>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`${styles.input} ${
                formData.email ? styles.filled : ""
              } ${errors.email ? styles.errorInput : ""}`}
            />
            <label
              htmlFor="email"
              className={`${styles.label} ${
                errors.email ? styles.errorLabel : ""
              }`}
            >
              Email <span className={styles.required}>*</span>
            </label>
          </div>
          {errors.email && (
            <span className={styles.errorMessage}>{errors.email}</span>
          )}
        </div>
        <div className={styles.field}>
          <MuiTelInput
            // Phone config
            defaultCountry={"FR"}
            forceCallingCode
            langOfCountryName={"fr"}
            name="phone"
            id="phone"
            label="Téléphone"
            value={formData.phone}
            onChange={(_, { numberValue: value }) => {
              // MuiTelInput onChange does not provide an event, so apply directly the value
              setFormData((prevData) => ({
                ...prevData,
                phone: value || "",
              }));
              setErrors((prevErrors) => ({
                ...prevErrors,
                phone: "",
              }));
            }}
            error={Boolean(errors.phone)}
            helperText={errors.phone}
            required
            InputProps={{
              required: false, // We do not want default validation, only label 'required' asterisk
            }}
            sx={{
              color: "var(--nuvee-text-color)",
              "& .MuiInputBase-input": {
                padding: "13px 14px",
              },
              "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-notchedOutline:hover": {
                borderColor: '#CCC',
              },
              "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: 'var(--nuvee-primary-color)',
                borderWidth: '1px',
              },
              "& .MuiFormLabel-root": {
                fontWeight: "600",
                "&.Mui-focused": {
                  color: "var(--nuvee-primary-color)",
                },
                "& .MuiFormLabel-asterisk": {
                  color: "#ff0000",
                },
              }
            }}
          />
        </div>
      </div>

      <div className={styles.group}>
        <div className={styles.field}>
          <div className={styles.inputContainer}>
            <select
              id="job"
              name="job"
              value={formData.job}
              onChange={handleChange}
              className={`${styles.select} ${styles.filled} ${
                errors.job ? styles.errorInput : ""
              }`}
            >
              <option value="" disabled>
                Choisir dans la liste...
              </option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.name}
                </option>
              ))}
            </select>
            <label
              htmlFor="job"
              className={`${styles.label} ${
                errors.job ? styles.errorLabel : ""
              }`}
            >
              Profession <span className={styles.required}>*</span>
            </label>
          </div>
          {errors.job && (
            <span className={styles.errorMessage}>{errors.job}</span>
          )}
        </div>
        {specialities.length > 0 && (
          <div className={styles.field}>
            <div className={styles.inputContainer}>
              <select
                id="speciality"
                name="speciality"
                value={formData.speciality}
                onChange={handleChange}
                className={`${styles.select} ${styles.filled} ${
                  errors.speciality ? styles.errorInput : ""
                }`}
              >
                <option value="" disabled>
                  Choisir dans la liste...
                </option>
                {specialities.map((speciality) => (
                  <option key={speciality.id} value={speciality.id}>
                    {speciality.name}
                  </option>
                ))}
              </select>
              <label
                htmlFor="speciality"
                className={`${styles.label} ${
                  errors.speciality ? styles.errorLabel : ""
                }`}
              >
                Spécialité <span className={styles.required}>*</span>
              </label>
            </div>
            {errors.speciality && (
              <span className={styles.errorMessage}>{errors.speciality}</span>
            )}
          </div>
        )}
        <div
          className={`${styles.field} ${
            specialities.length ? styles.fullWidth : ""
          }`}
        >
          <div className={styles.inputContainer}>
            <input
              type="text"
              id="structure"
              name="structure"
              value={formData.structure}
              onChange={handleChange}
              className={`${styles.input} ${
                formData.structure ? styles.filled : ""
              } ${errors.structure ? styles.errorInput : ""}`}
            />
            <label
              htmlFor="structure"
              className={`${styles.label} ${
                errors.structure ? styles.errorLabel : ""
              }`}
            >
              Structure <span className={styles.required}>*</span>
            </label>
          </div>
          {errors.structure && (
            <span className={styles.errorMessage}>{errors.structure}</span>
          )}
        </div>
      </div>

      <div className={styles.group}>
        <div className={styles.field}>
          <div className={styles.inputContainer}>
            <AddressInput
              value={formData.address}
              onChange={handleAddressChange}
              error={errors.address}
            />
          </div>
        </div>
      </div>

      <div className={styles.checkboxGroup}>
        <label
          htmlFor="isProfessional"
          className={`${styles.checkboxLabel} ${
            errors.isProfessional ? styles.errorLabel : ""
          }`}
        >
          <input
            type="checkbox"
            id="isProfessional"
            name="isProfessional"
            value={formData.isProfessional}
            onChange={handleChange}
            className={styles.checkbox}
          />
          <span className={styles.customCheckbox}></span>
          <span>
            En cochant cette case, je certifie sur l&apos;honneur être un&nbsp;
            <span className={styles.highlight}>professionnel de santé</span> et
            l&apos;exactitude des informations communiquées.&nbsp;
            <span className={styles.required}>*</span>
          </span>
        </label>
        {errors.isProfessional && (
          <span className={styles.errorMessage}>{errors.isProfessional}</span>
        )}

        <label
          className={`${styles.checkboxLabel} ${
            errors.acceptCGV ? styles.errorLabel : ""
          }`}
        >
          <input
            type="checkbox"
            id="acceptCGV"
            name="acceptCGV"
            value={formData.acceptCGV}
            onChange={handleChange}
            className={styles.checkbox}
          />
          <span className={styles.customCheckbox}></span>
          <span>
            En cochant cette case, j&apos;accepte et je reconnais avoir pris
            connaissance des{" "}
            <a className={styles.highlight} href="https://nuvee.fr/informations-legales/" target="_blank">
              conditions générales de vente.
            </a>
            <span className={styles.required}> *</span>
          </span>
        </label>
        {errors.acceptCGV && (
          <span className={styles.errorMessage}>{errors.acceptCGV}</span>
        )}
      </div>

      <button
        type="submit"
        className={styles.submitButton}
        disabled={isSubmitting}
      >
        {(!isDialogOpen && isSubmitting) ? (
          <>
            Validation en cours{" "}
            <div className={styles.buttonLoader}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 50 50"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.spinner}
              >
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="5"
                  stroke="#fff"
                  strokeLinecap="round"
                  strokeDasharray="90, 150"
                  strokeDashoffset="0"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    dur="1.5s"
                    values="-35; 175"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
          </>
        ) : (
          "Procéder au paiement"
        )}
      </button>
    </form>
  );
}

export default InformationsForm;
